<template>
    <div>
        <div class="blockly-div" ref="blocklyDiv"></div>
        <xml ref="blocklyToolbox" style="display: none">
            <slot></slot>
        </xml>
    </div>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
*/
/**
 * @license
 *
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @fileoverview Blockly Vue Component.
 * @author samelh@google.com (Sam El-Husseini)
 */

import Blockly from "blockly";
import { AreaService } from "@/services/area.service";
import { AuthService } from "@/services/auth.service";
import { toolboxXML, toolboxXML_noDownload } from "../blocks/toolbox";
import  Msg from  '@/msg/en.js' ;
import { NavigationController } from '@blockly/keyboard-navigation';
import { getFeaturedWorkflows } from '@/blocks/featured_workflows';
import { ConfigService } from '@/services/config.service';
import * as configConstants from "@/config/constants";
import { OrgConfig } from "@/config/main";
import { validateWorkspace } from "@/blocks/workspace_validation";
import {FEATURED_WORKFLOW_BLOCK_PREFIX } from '@/blocks/featured_workflows/utils';
import { ImageService } from "@/services/image.service";

export default {
    name: "BlocklyComponent",
    props: ["value", "loadWorkflow"],
    data() {
        return {
            workspace: null,
            user: {},
            options: {
                media: "media/",
                toolbox: ``,
                sounds: true,
                scrollbars: true,
                horizontalLayout: false,
                trashcan: true,
                renderer: 'colours_renderer',
                theme: '',
                grid:{
                    spacing: 20,
                    length: 0,
                    snap: true
                },
                zoom: {
                    controls: true,
                    wheel: true,
                    startScale: 0.8,
                    maxScale: 2,
                    minScale: 0.3,
                    scaleSpeed: 1.2,
                    pinch: true
                },
            },
            hasDownload: this.downloadsEnabled,
            toolboxContent: "",
            orgConfig: "",
            toolboxDownloadContent: "",
            uiEvents: [],
            moveEvents: [],
            featuredWorkflowsArray: [],
        };
    },
    watch: {
        // Watcher on toolboxContent; when it updates, the toolbox XML is updated
        toolboxContent: function () {
            console.info("Updating toolbox to pick up changes");
            this.insertToolbox();
        },
        loadWorkflow: function (newVal) {
            this.loadWorkflow = newVal;
            this.loadWorkflowFromUrl(newVal);
        },
        value: {
            deep:true,
            immediate: true,
            handler() {
                if(this.workspace){
                    validateWorkspace(this.workspace);
                }
            }
        }
    },
    mounted() {
        Blockly.Msg = Msg ;

        this.options.theme = Blockly.Themes.PurpleYellowTeal;

        this.authsubscription = AuthService.loggedUser$.subscribe((user) => {
            this.user = user;

            // Get the OrgConfig object and use it to set the Blockly toolbox XML.
            // Note that the call to getOrgConfig will return a fully specified OrgConfig,
            // ready to use with all properties loaded from Firestore.
            ConfigService.getOrgConfig(this.user.orgId).then(function(orgConfig) {
                this.orgConfig = orgConfig;
                this.toolboxContent = this.orgConfig.getConfig(configConstants.CONFIG_TYPE.TOOLBOX);
                this.featuredWorkflowsArray = getFeaturedWorkflows(orgConfig)
                this.loadWorkflowFromUrl(this.loadWorkflow);
            }.bind(this));
        });

        this.areasubscription = AreaService.shapeUpdated$.subscribe(() => {
            let blocks = this.workspace.getTopBlocks();
            blocks.forEach((block) => {
                this.updateBlockData(block.id);
            });
        });

        this.eventsubscription = AreaService.featuredBlockEvent$.subscribe(function(params) {
            if(params.isAreaOnly !== true){
                let xml = params.xml;
                // continue with loading the featured blocks here once we've cleared the left over user areas. it's to do with asynchronous timings
                let testVar = Blockly.Xml.textToDom(xml);
                Blockly.Xml.domToWorkspace(testVar, this.workspace);
            }
        }.bind(this));
    },
    beforeDestroy() {
        this.areasubscription.unsubscribe();
        this.authsubscription.unsubscribe();
        this.eventsubscription.unsubscribe();
    },
    methods: {
        loadWorkflowFromUrl(identifier) {
            if (identifier !== '' && typeof identifier !== 'undefined') {
                let xmlData = null;
                let areaData = null;
                let mapType = null;
                let isAreaOnly = null;
                if (this.featuredWorkflowsArray) {
                    this.featuredWorkflowsArray.forEach(function(item) {
                        if (identifier === item.id) {
                            xmlData = item.xml;
                            areaData = item.area;
                            mapType = item.mapType;
                            isAreaOnly = item.isAreaOnly;
                        }
                    }.bind(this));
                }
                
                if (xmlData !== null ) {
                    this.loadFeaturedBlock(xmlData, areaData, mapType, isAreaOnly);
                }
            }
        },
        loadFeaturedBlock(xml, area, mapType, isAreaOnly) {
            this.workspace.clear();
            // create an object to pass into the publish polygon function to create our custom areas
            const params = {
                area: area,
                xml: xml,
                mapType: mapType,
                isAreaOnly: isAreaOnly
            }
            // the conditional here is so we can skip the journey that would require us to upload a custom area with a featured workflow
            // if we need to add a custom area specified by the workflow we need to make use of Area.Service.publishPolygon
            // if not, we can skip that interim step and render the workflow on the workspace
            // refactored so we read the default workflow URL from orgConfig
            if (this.defaultWorkflowUrl.includes('strata')) {
                AreaService.publishFeatureEvent(params);
            } else {
                AreaService.publishPolygon(params);
            }
        },
        insertToolbox() {
            if (!this.toolboxContent) {
                console.info("toolboxContent not yet loaded; not rendering toolbox");
                return;
            }

            let tempToolbox = Blockly.Xml.textToDom(this.toolboxContent);
            // xml to string but in the "nice" format blockly accepts
            const xmlStr = Blockly.Xml.domToPrettyText(tempToolbox);

            this.options.toolbox = xmlStr;

            // inject toolbox from file if db call fails
            var options = this.options || {};
            if (!options.toolbox) {
                this.hasDownload
                ? options.toolbox = toolboxXML
                : options.toolbox = toolboxXML_noDownload
            }

            // inject toolbox into workspace
            this.workspace = Blockly.inject(this.$refs["blocklyDiv"], options);

            this.$emit("newWorkspace", this.workspace);
            this.workspace.addChangeListener(this.onWorkspaceChange);
            ImageService.registerWorkspace(this.workspace);

            //update workspace user
            this.workspace.currentUser = this.user;

            // register button action to open the trial request link
            this.workspace.registerButtonCallback('registerWithEBX', function() {
                window.open("https://blox.earth/#demo");
            });

            // keyboad navigation plugin code
            const navigationController = new NavigationController();
            navigationController.init();
            navigationController.addWorkspace(this.workspace);
            // EO --- keyboad navigation plugin code
        },
        onWorkspaceChange(event) {
            this.$emit("newWorkspace", this.workspace);

            // updated to the new ui events manager as per https://groups.google.com/g/blockly/c/4StIgJJXPwY/m/VSHqnqADAgAJ
            if(event.isUiEvent){
                this.uiEvents.push(event)
                if(this.uiEvents.length>2){
                    this.uiEvents.shift();
                }
                return;
            }

            switch (event.type) {
                case "create": {
                    let block = this.workspace.getBlockById(event.blockId);
                    let blockData = this.createBlockData(block);
                    this.value.push(blockData);

                    // if we're adding a new block that is not a featured block
                    if (this.value.length > 1 && !event.blockId.includes(FEATURED_WORKFLOW_BLOCK_PREFIX)) {
                        // Dispose of any featured workflow blocks if required
                        if (!this.featuredWorkflowsModifiable) {
                            this.value.forEach(function(block) {
                                // check if block is featured workflow
                                if (block.id.includes(FEATURED_WORKFLOW_BLOCK_PREFIX)) {
                                    let blockToDelete = this.workspace.getBlockById(block.id)
                                    blockToDelete.dispose();
                                }
                            }.bind(this));
                        }
                    }

                    break;
                }
                case "move": {
                    // adds all move events to array list of events
                    this.moveEvents.push(event);
                    if (this.moveEvents.length > 2) {
                        this.moveEvents.shift();
                    }

                    if (event.newParentId) {
                        let existingTopLevelBlockIndex = this.value.findIndex(
                            (b) => b.id == event.blockId
                        );

                        // splice is cyclic
                        if(existingTopLevelBlockIndex !== -1){
                            this.value.splice(existingTopLevelBlockIndex, 1);
                        }
                        this.updateBlockData(event.newParentId);
                    }

                    if (event.oldParentId) {
                        if (!event.newParentId) {
                            let block = this.workspace.getBlockById(
                                event.blockId
                            );
                            if (block) {
                                let blockData = this.createBlockData(block);
                                this.value.push(blockData);
                            }
                        }
                        this.updateBlockData(event.oldParentId);
                    }

                    // captures special case where first move event (dragStart) doesn't correctly update the old parent block as it detects it still has children
                    // in the initial update call, so it has to be updated a second time

                    // if child has been moved to empty space/ new parent block
                    if (
                        (!event.oldParentId && !event.newParentId) ||
                        (!event.oldParentId && event.newParentId)
                    ) {
                        // check to see if events are linked i.e. drag start followed by drag stop
                        if (
                            this.uiEvents.length > 1 &&
                            this.uiEvents[0].isStart === true &&
                            this.uiEvents[1].isStart === false
                        ) {
                            let lastMoveEvent = this.moveEvents[0];
                            if (lastMoveEvent) {
                                // if the last move i.e drag start has an oldParentId and we know the new one does not - update old parent
                                if (lastMoveEvent.oldParentId) {
                                    this.updateBlockData(
                                        lastMoveEvent.oldParentId
                                    );
                                }
                            }
                        }
                    }
                    break;
                }
                case "delete": {
                    let existingTopLevelBlockIndex = this.value.findIndex(b => b.id == event.blockId);

                    if (existingTopLevelBlockIndex == -1) {
                        break;
                    }

                    // check for special case in which a a block is the data selector, remove bbox with its id
                    if(this.value[existingTopLevelBlockIndex].type === "output_data_selector"){
                        AreaService.publishUpdatedDatasetBbox(null, event.blockId);                       
                    }

                    // remove top block from array
                    this.value.splice(existingTopLevelBlockIndex, 1);

                    break;
                }
                case "change": {
                    this.updateBlockData(event.blockId);
                    break;
                }
            }
        },
        updateBlockData(blockId) {
            let block = this.workspace.getBlockById(blockId);
            if (!block || !block.rendered) {
                return;
            }
            let topBlock = block.getRootBlock();
            topBlock.currentUser = this.user;
            let existingBlockIndex = this.value.findIndex(
                (b) => b.id == topBlock.id
            );
            if (existingBlockIndex !== -1) {
                let existingValue = this.value[existingBlockIndex];
                existingValue.runResult = {};
            }
        },
        createBlockData(block) {
            return {
                id: block.id,
                type: block.type,
                runResult: {},
            };
        },
    },
    computed: {
        featuredWorkflowsModifiable() {
            return OrgConfig.isFeatureShown(this.orgConfig, configConstants.FEATURES.WORKFLOWS_MODIFIABLE);
        },
        orgCustomisations() {
            return this.orgConfig ? this.orgConfig.getConfig(configConstants.CONFIG_TYPE.CUSTOMISATION) : undefined;
        },
        defaultWorkflowUrl() {
            if (this.orgCustomisations) {
                return this.orgCustomisations['url.path.default.workflow'] ? this.orgCustomisations['url.path.default.workflow'] : '';
            } else {
                return '';
            }
            
        },
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.blockly-div {
    height: 100%;
    width: 100%;
    text-align: left;
    flex-grow: 1;
}

.blocklyFlyoutBackgroundtt {
    fill: whitesmoke;
    fill-opacity: 1;
}
</style>
